import { useMemo } from "react";
import { useTelemetryVisualization } from "./useTelemetry";

interface VehiclePosition {
  lat: number;
  lng: number;
  speed?: number;
  timestamp: number;
}

export function useVehiclePosition(
  sessionId: string,
  currentTelemetryTime: number, // Time in seconds from telemetry start
  enabled = true,
): {
  position: VehiclePosition | null;
  positionTrail: VehiclePosition[];
  isLoading: boolean;
  error: unknown;
} {
  // Get all telemetry data (GPS coordinates)
  const { allData, isLoading, error } = useTelemetryVisualization(
    sessionId,
    ["GPS Latitude", "GPS Longitude", "GPS Speed"], // Include GPS channels
    undefined, // No time range filter
    20000, // Higher point limit for position tracking
  );

  const { position, positionTrail } = useMemo(() => {
    if (!allData || allData.length === 0 || !enabled) {
      return { position: null, positionTrail: [] };
    }

    // Check if we have GPS data in the telemetry
    const hasGpsLatitude = "GPS Latitude" in (allData[0]?.values || {});
    const hasGpsLongitude = "GPS Longitude" in (allData[0]?.values || {});

    if (!hasGpsLatitude || !hasGpsLongitude) {
      return { position: null, positionTrail: [] };
    }

    // Find the closest data point to the current telemetry time
    const findClosestDataPoint = (targetTime: number) => {
      if (allData.length === 0) return null;

      // Binary search for efficiency with large datasets
      let left = 0;
      let right = allData.length - 1;
      let closest = allData[0];

      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const point = allData[mid];

        if (
          Math.abs(point.timestamp - targetTime) <
          Math.abs(closest.timestamp - targetTime)
        ) {
          closest = point;
        }

        if (point.timestamp < targetTime) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }

      return closest;
    };

    const currentDataPoint = findClosestDataPoint(currentTelemetryTime);

    if (!currentDataPoint) {
      return { position: null, positionTrail: [] };
    }

    // Extract GPS coordinates and speed from the data point
    const lat = currentDataPoint.values["GPS Latitude"];
    const lng = currentDataPoint.values["GPS Longitude"];
    const speed = currentDataPoint.values["GPS Speed"];

    // Basic GPS coordinate validation
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return { position: null, positionTrail: [] };
    }

    // Check if coordinates are meaningful (not zeros or clearly invalid)
    if (lat === 0 && lng === 0) {
      return { position: null, positionTrail: [] };
    }

    const currentPosition = {
      lat,
      lng,
      speed,
      timestamp: currentDataPoint.timestamp,
    };

    // Create position trail (last 30 seconds of positions)
    const trailStartTime = currentTelemetryTime - 30; // 30 seconds ago
    const trail: VehiclePosition[] = [];

    // Sample positions every 1 second for the trail
    for (let t = trailStartTime; t <= currentTelemetryTime; t += 1) {
      const trailDataPoint = findClosestDataPoint(t);

      if (trailDataPoint) {
        const trailLat = trailDataPoint.values["GPS Latitude"];
        const trailLng = trailDataPoint.values["GPS Longitude"];
        const trailSpeed = trailDataPoint.values["GPS Speed"];

        // Validate trail coordinates
        if (
          trailLat >= -90 &&
          trailLat <= 90 &&
          trailLng >= -180 &&
          trailLng <= 180 &&
          !(trailLat === 0 && trailLng === 0)
        ) {
          trail.push({
            lat: trailLat,
            lng: trailLng,
            speed: trailSpeed,
            timestamp: trailDataPoint.timestamp,
          });
        }
      }
    }

    return {
      position: currentPosition,
      positionTrail: trail,
    };
  }, [allData, currentTelemetryTime, enabled]);

  return {
    position,
    positionTrail,
    isLoading,
    error,
  };
}
